import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { toPng } from 'html-to-image'
import { Download, Lightbulb, ArrowRight, X, Maximize2, GitCompare } from 'lucide-react'
import type { InfographicConcept } from '../../types'
import { copyImageToClipboard } from '../../lib/clipboard'
import { useUIStore } from '../../stores/uiStore'
import { useAnalyticsStore } from '../../stores/analyticsStore'
import { useInfographicStore, type InfographicStyle } from '../../stores/infographicStore'
import { useChatStore } from '../../stores/chatStore'
import { submitQuery } from '../../lib/api'
import CopyImageButton from './CopyImageButton'
import StyleChooserDropdown from './StyleChooserDropdown'
import InfographicDiffView from './InfographicDiffView'

interface InfographicTabProps {
  infographic?: InfographicConcept
}

export default function InfographicTab({ infographic }: InfographicTabProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showDiffView, setShowDiffView] = useState(false)
  const showToast = useUIStore((state) => state.showToast)
  const trackDownload = useAnalyticsStore((state) => state.trackDownload)

  // Infographic store for version management
  const addVersion = useInfographicStore((state) => state.addVersion)
  const isRegenerating = useInfographicStore((state) => state.isRegenerating)
  const setIsRegenerating = useInfographicStore((state) => state.setIsRegenerating)
  const currentVersion = useInfographicStore((state) => state.getCurrentVersion())
  const versions = useInfographicStore((state) => state.versions)

  // Get the latest query from chat for regeneration
  const messages = useChatStore((state) => state.messages)
  const lastUserMessage = messages.filter(m => m.role === 'user').slice(-1)[0]

  // Sync current infographic to version history when it changes
  useEffect(() => {
    if (infographic && (infographic.title || infographic.imageBase64)) {
      // Only add if this isn't already the current version
      if (!currentVersion || currentVersion.infographic !== infographic) {
        addVersion(infographic)
      }
    }
  }, [infographic, currentVersion, addVersion])

  // Display current version if available, otherwise fallback to prop
  const displayInfographic = currentVersion?.infographic || infographic

  // Handle ESC key and body scroll lock for modal
  useEffect(() => {
    if (isModalOpen) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsModalOpen(false)
      }

      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = ''
      }
    }
  }, [isModalOpen])

  // Handle regeneration with selected style
  const handleRegenerate = async (style: InfographicStyle) => {
    if (!lastUserMessage) {
      showToast('No query found to regenerate', 'error')
      return
    }

    setIsRegenerating(true)
    try {
      const response = await submitQuery({
        question: `Please regenerate the infographic for: ${lastUserMessage.content}`,
        infographicStyle: style
      })

      if (response.infographic) {
        addVersion(response.infographic, style)
        showToast(`Infographic regenerated with "${style}" style`, 'success')
      }
    } catch (error) {
      console.error('Failed to regenerate infographic:', error)
      showToast('Failed to regenerate infographic', 'error')
    } finally {
      setIsRegenerating(false)
    }
  }

  // If showing diff view, render it
  if (showDiffView) {
    return (
      <div role="tabpanel" className="py-8 px-6">
        <InfographicDiffView versions={versions} onClose={() => setShowDiffView(false)} />
      </div>
    )
  }

  // If no infographic data, show coming soon
  if (!displayInfographic || (!displayInfographic.title && !displayInfographic.imageBase64)) {
    return (
      <div
        role="tabpanel"
        className="flex flex-col items-center justify-center py-12 px-6 text-center space-y-4"
      >
        <img
          src="/shellby-studying.png"
          alt="Shellby studying"
          className="w-32 h-32 object-contain"
        />
        <h3 className="text-xl font-bold text-[var(--bbs-text-primary)]">
          Coming Soon!
        </h3>
        <p className="text-[var(--bbs-text-secondary)] max-w-sm">
          Shellby is learning to create beautiful infographics for your support responses.
          Check back soon!
        </p>
        <div className="mt-4 px-4 py-2 bg-[var(--bbs-color-accent)]/10 border border-[var(--bbs-color-accent)]/30 rounded-full">
          <span className="text-sm text-[var(--bbs-color-accent)]">
            On the roadmap
          </span>
        </div>
      </div>
    )
  }

  // Future: If image exists, display image
  if (displayInfographic.imageBase64 && displayInfographic.mimeType) {
    const handleDownloadImage = async () => {
      setIsDownloading(true)
      try {
        const link = document.createElement('a')
        link.download = `infographic-${Date.now()}.png`
        link.href = `data:${displayInfographic.mimeType};base64,${displayInfographic.imageBase64}`
        link.click()

        showToast('Infographic downloaded successfully', 'success')
        trackDownload('infographic')
      } catch (error) {
        console.error('Failed to download infographic:', error)
        showToast('Failed to download infographic', 'error')
      } finally {
        setIsDownloading(false)
      }
    }

    const handleCopyImage = async () => {
      try {
        const base64String = `data:${displayInfographic.mimeType};base64,${displayInfographic.imageBase64}`
        await copyImageToClipboard(base64String, displayInfographic.mimeType)
        showToast('Infographic copied to clipboard', 'success')
      } catch (error) {
        console.error('Failed to copy infographic:', error)
        showToast('Failed to copy image. Try downloading instead.', 'error')
      }
    }

    return (
      <>
        <div
          role="tabpanel"
          className="flex flex-col items-center justify-center py-8 px-6 space-y-6"
        >
          {isRegenerating && (
            <div className="w-full max-w-2xl bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] rounded-2xl p-12 animate-pulse">
              <div className="text-center text-[var(--bbs-text-secondary)]">
                <div className="text-lg font-medium mb-2">Regenerating infographic...</div>
                <div className="text-sm">This may take a few moments</div>
              </div>
            </div>
          )}
          {!isRegenerating && (
            <div className="w-full max-w-2xl relative group cursor-pointer" onClick={() => setIsModalOpen(true)}>
              <img
                src={`data:${displayInfographic.mimeType};base64,${displayInfographic.imageBase64}`}
                alt={displayInfographic.title || 'Support infographic'}
                className="w-full h-auto rounded-2xl shadow-[0_0_20px_rgba(188,0,36,0.2)] transition-transform group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-2xl backdrop-blur-sm">
                <div className="bg-white/90 rounded-full p-4 shadow-xl">
                  <Maximize2 className="w-8 h-8 text-[var(--bbs-color-accent)]" />
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-[var(--bbs-text-tertiary)] text-center italic max-w-xl">
            This infographic is for illustrative purposes only and may contain errors.
            Always refer to official support resources for the most accurate information.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <StyleChooserDropdown onSelectStyle={handleRegenerate} disabled={isRegenerating} />
            {versions.length >= 2 && (
              <button
                type="button"
                onClick={() => setShowDiffView(true)}
                disabled={isRegenerating}
                className="btn btn-glass"
                title="Compare versions"
              >
                <GitCompare className="w-4 h-4" />
                Compare Versions
              </button>
            )}
            <CopyImageButton onCopy={handleCopyImage} disabled={isRegenerating} />
            <button
              type="button"
              onClick={handleDownloadImage}
              disabled={isDownloading || isRegenerating}
              className="btn btn-primary"
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'Downloading...' : 'Download PNG'}
            </button>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/80 backdrop-blur-md overflow-y-auto p-4 sm:p-8"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsModalOpen(false)
            }}
          >
            <div className="relative w-full max-w-4xl mx-auto my-8 sm:my-12">
              {/* Close button */}
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              {/* Image */}
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
                <img
                  src={`data:${displayInfographic.mimeType};base64,${displayInfographic.imageBase64}`}
                  alt={displayInfographic.title || 'Support infographic'}
                  className="w-full h-auto"
                />
              </div>

              {/* Controls */}
              <div className="card p-6 space-y-4">
                <p className="text-xs text-[var(--bbs-text-tertiary)] text-center italic">
                  This infographic is for illustrative purposes only and may contain errors.
                  Always refer to official support resources for the most accurate information.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <StyleChooserDropdown onSelectStyle={handleRegenerate} disabled={isRegenerating} />
                  {versions.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false)
                        setShowDiffView(true)
                      }}
                      disabled={isRegenerating}
                      className="btn btn-glass"
                      title="Compare versions"
                    >
                      <GitCompare className="w-4 h-4" />
                      Compare Versions
                    </button>
                  )}
                  <CopyImageButton onCopy={handleCopyImage} disabled={isRegenerating} />
                  <button
                    type="button"
                    onClick={handleDownloadImage}
                    disabled={isDownloading || isRegenerating}
                    className="btn btn-primary"
                  >
                    <Download className="w-4 h-4" />
                    {isDownloading ? 'Downloading...' : 'Download PNG'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    )
  }

  // Current: Display text concept card
  const handleDownloadPNG = async () => {
    if (!cardRef.current) return

    setIsDownloading(true)
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: 'transparent'
      })

      const link = document.createElement('a')
      link.download = `infographic-${Date.now()}.png`
      link.href = dataUrl
      link.click()

      showToast('Infographic downloaded successfully', 'success')
      trackDownload('infographic')
    } catch (error) {
      console.error('Failed to download infographic:', error)
      showToast('Failed to download infographic', 'error')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCopyConceptCard = async () => {
    if (!cardRef.current) return

    try {
      await copyImageToClipboard(cardRef.current)
      showToast('Infographic image copied to clipboard', 'success')
    } catch (error) {
      console.error('Failed to copy infographic:', error)
      showToast('Failed to copy image. Try downloading instead.', 'error')
    }
  }

  return (
    <div
      role="tabpanel"
      className="flex flex-col items-center justify-center py-8 px-6"
    >
      {/* Loading Skeleton */}
      {isRegenerating && (
        <div className="w-full max-w-2xl bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] rounded-2xl p-12 animate-pulse">
          <div className="text-center text-[var(--bbs-text-secondary)]">
            <div className="text-lg font-medium mb-2">Regenerating infographic...</div>
            <div className="text-sm">This may take a few moments</div>
          </div>
        </div>
      )}

      {/* Infographic Card */}
      {!isRegenerating && (
        <div
          ref={cardRef}
          className="w-full max-w-2xl p-8 rounded-2xl bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] shadow-[0_0_20px_rgba(188,0,36,0.2)] space-y-6"
        >
          {/* Title */}
          <h2 className="text-2xl font-bold text-[#bc0024] text-center">
            {displayInfographic.title || 'Support Information'}
          </h2>

          {/* Key Visuals */}
          {displayInfographic.keyVisuals && displayInfographic.keyVisuals.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#bc0024]">
              Key Visuals
            </h3>
            <ul className="space-y-2 pl-2">
              {displayInfographic.keyVisuals.map((visual, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-[var(--bbs-text-secondary)]"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#bc0024] flex-shrink-0" />
                  <span>{visual}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Primary Takeaway */}
        {displayInfographic.primaryTakeaway && (
          <div className="p-4 rounded-xl bg-red-50 border-2 border-[#bc0024]">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-[#bc0024] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#bc0024] mb-2">
                  Primary Takeaway
                </h3>
                <p className="text-[var(--bbs-text-primary)]">
                  {displayInfographic.primaryTakeaway}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        {displayInfographic.cta && (
          <div className="flex items-center gap-3 text-[var(--bbs-text-secondary)]">
            <ArrowRight className="w-5 h-5 text-[#bc0024] flex-shrink-0" />
            <p>{displayInfographic.cta}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="pt-4 border-t border-[var(--bbs-color-border)]">
          <p className="text-xs text-[var(--bbs-text-tertiary)] text-center italic">
            This infographic is for illustrative purposes only and may contain errors.
            Always refer to official support resources for the most accurate information.
          </p>
        </div>
      </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mt-6 justify-center">
        <StyleChooserDropdown onSelectStyle={handleRegenerate} disabled={isRegenerating} />
        {versions.length >= 2 && (
          <button
            type="button"
            onClick={() => setShowDiffView(true)}
            disabled={isRegenerating}
            className="btn btn-glass"
            title="Compare versions"
          >
            <GitCompare className="w-4 h-4" />
            Compare Versions
          </button>
        )}
        <CopyImageButton onCopy={handleCopyConceptCard} disabled={isRegenerating} />
        <button
          type="button"
          onClick={handleDownloadPNG}
          disabled={isDownloading || isRegenerating}
          className="btn btn-primary"
        >
          <Download className="w-4 h-4" />
          {isDownloading ? 'Downloading...' : 'Download PNG'}
        </button>
      </div>
    </div>
  )
}
